import React, { useState, useEffect, useMemo } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps, DataPoint, StackedDataPoint, PivotedApiResponse, SimpleApiResponse } from "./types";
import { calculateTrendline, formatDateInTimezone } from "./utils";
import StackedBarChart from "../../../components/analytics/StackedBarChart";
import { WidgetCard } from "../../../components/analytics";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const CC: any = ComposedChart as any;
const BR: any = Bar as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

/* eslint-disable camelcase */
type LegacyStackedApiResponseItem = {
    key: string;
    count: number;
    [focusField: string]: string | number;
};
/* eslint-enable camelcase */

/**
 * Mapping from frontend focus field names to the actual API response field names.
 * The backend SQL uses different column names (e.g., custom_utm_sourceiq) but the
 * frontend uses semantic names (e.g., attribution_id).
 * 
 * @deprecated This mapping is only needed for legacy backends that don't pivot data.
 * Once all backends use _pivot_focus_data(), this can be removed.
 */
const FOCUS_FIELD_TO_API_FIELD: Record<string, string> = {
    "attribution_id": "custom_utm_sourceiq",
    "device_platform": "device_platform",
    "brand_id": "brand_id"
};

/**
 * Transform flat API response into pivoted format for stacked bar chart.
 * 
 * @deprecated This function is only needed for legacy backends that don't pivot data.
 * Once all backends use _pivot_focus_data(), this can be removed.
 * 
 * Input: [{ key: "2026-01-23", custom_utm_sourceiq: "campaign123", count: 378 }, ...]
 * Output: { data: [{ key: "2026-01-23", campaign123: 378, Other: 334 }], focusValues: ["campaign123", "Other"] }
 */
const transformStackedDataLegacy = (
    items: LegacyStackedApiResponseItem[],
    focusField: string
): { data: StackedDataPoint[]; focusValues: string[] } => {
    const focusValuesSet = new Set<string>();
    const groupedByKey: Record<string, StackedDataPoint> = {};

    // Use the API field name that corresponds to the frontend focus field
    const apiFieldName = FOCUS_FIELD_TO_API_FIELD[focusField] || focusField;

    items.forEach(item => {
        const focusValue = String(item[apiFieldName] || "Unknown");
        focusValuesSet.add(focusValue);

        if (!groupedByKey[item.key]) {
            groupedByKey[item.key] = { key: item.key };
        }
        groupedByKey[item.key][focusValue] = item.count;
    });

    // Sort keys (dates) chronologically
    const sortedKeys = Object.keys(groupedByKey).sort();
    const data = sortedKeys.map(key => groupedByKey[key]);
    const focusValues = Array.from(focusValuesSet);

    return { data, focusValues };
};

/**
 * Type guard to check if response is pivoted format from new backend
 */
const isPivotedResponse = (response: unknown): response is PivotedApiResponse => {
    return (
        typeof response === "object" &&
        response !== null &&
        "pivoted" in response &&
        (response as PivotedApiResponse).pivoted === true &&
        "focus_values" in response &&
        Array.isArray((response as PivotedApiResponse).focus_values)
    );
};

/**
 * Play Widget Component - Play events data visualization
 */
const PlayWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions,
    timezone,
    onMetaLoaded
}) => {
    const [analyticsData, setAnalyticsData] = useState<DataPoint[]>([]);
    const [stackedData, setStackedData] = useState<StackedDataPoint[]>([]);
    const [focusValues, setFocusValues] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [widgetMeta, setWidgetMeta] = useState<any>(null);

    // Format date key for display based on timezone
    const formatXAxisTick = (dateKey: string): string => {
        return formatDateInTimezone(dateKey, timezone, "short");
    };

    // Calculate trendline for simple bar chart
    const dataWithTrendline = useMemo(() => {
        if (!chartOptions.showTrendline || analyticsData.length === 0) {
            return analyticsData;
        }
        const counts = analyticsData.map(d => d.count);
        const trendValues = calculateTrendline(counts, chartOptions.trendlineType);
        return analyticsData.map((d, i) => ({ ...d, _trendline: trendValues[i] }));
    }, [analyticsData, chartOptions.showTrendline, chartOptions.trendlineType]);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "play_events", // Widget key for PlayWidget
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandId,
                    filters.devicePlatform,
                    filters.focusField,
                    chartOptions.focusSize
                );
                
                if (filters.focusField) {
                    // Check if backend returned pre-pivoted data (new format)
                    if (isPivotedResponse(response)) {
                        // New format: backend already pivoted the data
                        setStackedData(response.result);
                        setFocusValues(response.focus_values);
                        setAnalyticsData([]);
                    } else {
                        // Legacy format: need to pivot on frontend
                        // This fallback can be removed once all backends are updated
                        const legacyResponse = response as unknown as { result: LegacyStackedApiResponseItem[] };
                        const items = Array.isArray(legacyResponse?.result) 
                            ? legacyResponse.result 
                            : [];
                        const { data, focusValues: fv } = transformStackedDataLegacy(
                            items,
                            filters.focusField
                        );
                        setStackedData(data);
                        setFocusValues(fv);
                        setAnalyticsData([]);
                    }
                } else {
                    // Handle regular data response
                    const simpleResponse = response as unknown as SimpleApiResponse;
                    const dataArray = Array.isArray(simpleResponse?.result)
                        ? simpleResponse.result
                        : [];
                    setAnalyticsData(dataArray);
                    setStackedData([]);
                    setFocusValues([]);
                }
                const meta = (response as any)?.widget;
                if (meta) {
                    setWidgetMeta(meta);
                    if (onMetaLoaded) {
                        onMetaLoaded({
                            widgetKey: "play_events",
                            displayName: meta.display_name,
                            description: meta.description,
                            schema: (response as any)?.schema
                        });
                    }
                }
            } catch (err) {
                Logger.error("Error fetching PlayWidget data", err);
                setError("Failed to fetch PlayWidget data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters, chartOptions.focusSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const isEmpty = !isLoading && !error && (
        filters.focusField ? stackedData.length === 0 : analyticsData.length === 0
    );

    return (
        <WidgetCard
            title={widgetMeta?.display_name || "Play Events Over Time"}
            description={widgetMeta?.description}
            isLoading={isLoading}
            error={error}
            isEmpty={isEmpty}
            theme={theme}
        >
            {/* Stacked bar chart when focusField is set */}
            {filters.focusField ? (
                <>
                    <StackedBarChart
                        data={stackedData}
                        focusField={filters.focusField}
                        focusValues={focusValues}
                        theme={theme}
                        showTrendline={chartOptions.showTrendline}
                        trendlineType={chartOptions.trendlineType}
                        timezone={timezone}
                    />
                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{
                            backgroundColor: "#f5f5f5",
                            padding: "15px",
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify({ data: stackedData, focusValues }, null, 2)}
                        </pre>
                    </details>
                </>
            ) : (
                <>
                    <div style={{
                        backgroundColor: theme.chartBg,
                        padding: "20px",
                        borderRadius: "5px",
                        marginBottom: "20px"
                    }}>
                        <RESP width="100%" height={400}>
                            <CC data={dataWithTrendline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CG strokeDasharray="3 3" />
                                <XA dataKey="key" tickFormatter={formatXAxisTick} />
                                <YA />
                                <TT labelFormatter={formatXAxisTick} />
                                <LG />
                                <BR dataKey="count" fill="#8884d8" name="Count" />
                                {chartOptions.showTrendline && (
                                    <LN
                                        type="monotone"
                                        dataKey="_trendline"
                                        stroke="#ff6b6b"
                                        strokeWidth={2}
                                        dot={false}
                                        name="Trend"
                                    />
                                )}
                            </CC>
                        </RESP>
                    </div>
                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{
                            backgroundColor: "#f5f5f5",
                            padding: "15px",
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(analyticsData, null, 2)}
                        </pre>
                    </details>
                </>
            )}
        </WidgetCard>
    );
};

export default PlayWidget;
